import { supabase } from '../lib/supabase'
import { encryptAES, decryptAES } from '../utils/encryption'

interface ProviderModel {
    name: string
    tier: 'economy' | 'balanced' | 'premium'
    maxTokens: number
    costPerInputToken: number
    costPerOutputToken: number
}

interface ProviderModels {
    provider: 'openai' | 'anthropic' | 'google'
    models: ProviderModel[]
}

// Catálogo completo de modelos por provedor
const PROVIDER_MODELS_CATALOG: ProviderModels[] = [
    {
        provider: 'openai',
        models: [
            {
                name: 'gpt-3.5-turbo',
                tier: 'economy',
                maxTokens: 4000,
                costPerInputToken: 0.0000015,
                costPerOutputToken: 0.000002
            },
            {
                name: 'gpt-4o-mini',
                tier: 'balanced',
                maxTokens: 8000,
                costPerInputToken: 0.00000015,
                costPerOutputToken: 0.0000006
            },
            {
                name: 'gpt-4o',
                tier: 'premium',
                maxTokens: 16000,
                costPerInputToken: 0.000005,
                costPerOutputToken: 0.000015
            }
        ]
    },
    {
        provider: 'anthropic',
        models: [
            {
                name: 'claude-3-haiku',
                tier: 'economy',
                maxTokens: 4000,
                costPerInputToken: 0.00000025,
                costPerOutputToken: 0.00000125
            },
            {
                name: 'claude-3-sonnet',
                tier: 'balanced',
                maxTokens: 8000,
                costPerInputToken: 0.000003,
                costPerOutputToken: 0.000015
            },
            {
                name: 'claude-3-opus',
                tier: 'premium',
                maxTokens: 16000,
                costPerInputToken: 0.000015,
                costPerOutputToken: 0.000075
            }
        ]
    },
    {
        provider: 'google',
        models: [
            {
                name: 'gemini-1.5-flash',
                tier: 'economy',
                maxTokens: 8000,
                costPerInputToken: 0.000000075,
                costPerOutputToken: 0.0000003
            },
            {
                name: 'gemini-1.5-pro',
                tier: 'premium',
                maxTokens: 32000,
                costPerInputToken: 0.00000125,
                costPerOutputToken: 0.000005
            }
        ]
    }
]

export interface UserModel {
    id: string
    user_id: string
    provider: string
    model_name: string
    tier: string
    is_active: boolean
    max_tokens: number
    cost_per_input_token: number
    cost_per_output_token: number
}

export class UserModelsService {
    /**
     * Ativa modelos quando usuário adiciona API Key
     */
    async activateProviderModels(
        userId: string,
        provider: 'openai' | 'anthropic' | 'google',
        apiKey: string
    ): Promise<{ success: boolean; modelsActivated: number }> {
        try {
            // 1. Salvar/Atualizar API Key
            const { error: providerError } = await supabase
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .from('user_llm_providers' as any)
                .upsert({
                    user_id: userId,
                    provider,
                    api_key_encrypted: this.encryptApiKey(apiKey),
                    is_active: true,
                    updated_at: new Date().toISOString()
                }, {
                    onConflict: 'user_id,provider'
                })

            if (providerError) {
                console.error('Error saving provider:', providerError)
                throw providerError
            }

            // 2. Buscar modelos do catálogo
            const providerCatalog = PROVIDER_MODELS_CATALOG.find(p => p.provider === provider)
            if (!providerCatalog) {
                throw new Error(`Provider ${provider} not found in catalog`)
            }

            // 3. Preparar modelos para inserir
            const modelsToInsert = providerCatalog.models.map(model => ({
                user_id: userId,
                provider,
                model_name: model.name,
                tier: model.tier,
                is_active: true,
                max_tokens: model.maxTokens,
                cost_per_input_token: model.costPerInputToken,
                cost_per_output_token: model.costPerOutputToken
            }))

            // 4. Inserir/Atualizar modelos
            const { error: modelsError } = await supabase
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .from('user_available_models' as any)
                .upsert(modelsToInsert, {
                    onConflict: 'user_id,provider,model_name'
                })

            if (modelsError) {
                console.error('Error saving models:', modelsError)
                throw modelsError
            }

            return { success: true, modelsActivated: modelsToInsert.length }
        } catch (error) {
            console.error('Error in activateProviderModels:', error)
            throw error
        }
    }

    /**
     * Desativa modelos quando usuário remove API Key
     */
    async deactivateProviderModels(
        userId: string,
        provider: 'openai' | 'anthropic' | 'google'
    ): Promise<{ success: boolean }> {
        try {
            // 1. Desativar provedor
            const { error: providerError } = await supabase
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .from('user_llm_providers' as any)
                .update({ is_active: false, updated_at: new Date().toISOString() })
                .eq('user_id', userId)
                .eq('provider', provider)

            if (providerError) {
                console.error('Error deactivating provider:', providerError)
                throw providerError
            }

            // 2. Desativar modelos
            const { error: modelsError } = await supabase
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .from('user_available_models' as any)
                .update({ is_active: false })
                .eq('user_id', userId)
                .eq('provider', provider)

            if (modelsError) {
                console.error('Error deactivating models:', modelsError)
                throw modelsError
            }

            return { success: true }
        } catch (error) {
            console.error('Error in deactivateProviderModels:', error)
            throw error
        }
    }

    /**
     * Busca modelos ativos do usuário
     */
    async getUserActiveModels(userId: string): Promise<UserModel[]> {
        try {
            const { data, error } = await supabase
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .from('user_available_models' as any)
                .select('*')
                .eq('user_id', userId)
                .eq('is_active', true)
                .order('cost_per_input_token', { ascending: true })

            if (error) {
                console.error('Error fetching active models:', error)
                throw error
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (data as any || []) as UserModel[]
        } catch (error) {
            console.error('Error in getUserActiveModels:', error)
            throw error
        }
    }

    /**
     * Busca provedores ativos do usuário
     */
    async getUserActiveProviders(userId: string): Promise<string[]> {
        try {
            const { data, error } = await supabase
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .from('user_llm_providers' as any)
                .select('provider')
                .eq('user_id', userId)
                .eq('is_active', true)

            if (error) {
                console.error('Error fetching active providers:', error)
                throw error
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return ((data as any) || []).map((p: any) => p.provider)
        } catch (error) {
            console.error('Error in getUserActiveProviders:', error)
            throw error
        }
    }

    /**
     * Criptografa API Key usando AES-256
     */
    private encryptApiKey(apiKey: string): string {
        return encryptAES(apiKey)
    }

    /**
     * Descriptografa API Key
     */
    async getDecryptedApiKey(
        userId: string,
        provider: 'openai' | 'anthropic' | 'google'
    ): Promise<string | null> {
        try {
            const { data, error } = await supabase
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .from('user_llm_providers' as any)
                .select('api_key_encrypted')
                .eq('user_id', userId)
                .eq('provider', provider)
                .eq('is_active', true)
                .single()

            if (error || !data) {
                return null
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return decryptAES((data as any).api_key_encrypted)
        } catch (error) {
            console.error('Error getting decrypted API key:', error)
            return null
        }
    }
}
